import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ArrowLeft, BellRinging, PaperPlaneTilt } from "@phosphor-icons/react";

/**
 * v1.28.03 — Portal → Settings → Notification Diagnostics.
 * Shows the last N push events for THIS device and a big Test button that
 * surfaces the rich `/api/push/test` response (payload sent + subscription
 * detail + on-device fix hint).
 */
export default function NotificationDiag() {
    const navigate = useNavigate();
    const [events, setEvents] = useState([]);
    const [busy, setBusy] = useState(false);
    const [lastTest, setLastTest] = useState(null);
    const [subEndpoint, setSubEndpoint] = useState("");

    useEffect(() => {
        // Read the current SW subscription so the test targets THIS device.
        (async () => {
            try {
                const reg = await navigator.serviceWorker?.ready;
                const sub = await reg?.pushManager.getSubscription();
                if (sub) setSubEndpoint(sub.endpoint);
            } catch { /* noop */ }
        })();
        loadEvents();
    }, []);

    const loadEvents = async () => {
        try {
            const { data } = await api.get("/notifications?limit=20");
            const rows = Array.isArray(data) ? data : (data.notifications || []);
            setEvents(rows.slice(0, 20));
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const sendTest = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/push/test", subEndpoint ? { endpoint: subEndpoint } : {});
            setLastTest(data);
            if (data.sent > 0) toast.success("Test push delivered — check your lock screen");
            else toast.error(data.detail || "Push not delivered");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <div className="space-y-4 p-4" data-testid="notification-diag-page">
            <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900">
                <ArrowLeft size={12} /> Back
            </button>
            <div>
                <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
                    <BellRinging size={22} weight="fill" className="text-indigo-600" /> Notification Diagnostics
                </h1>
                <p className="mt-1 text-sm text-slate-500">
                    Verify push notifications end-to-end. Tap Send Test — you should see a heads-up banner + hear the chime.
                </p>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">This device</div>
                        <div className="mt-0.5 font-mono text-[11px] text-slate-700 truncate max-w-md" title={subEndpoint}>
                            {subEndpoint ? subEndpoint.replace(/^https?:\/\//, "").slice(0, 60) + "…" : "No push subscription found. Enable notifications first from Settings."}
                        </div>
                    </div>
                    <Button data-testid="send-test-push" disabled={busy || !subEndpoint} onClick={sendTest} className="bg-indigo-600 text-white hover:bg-indigo-700">
                        <PaperPlaneTilt size={14} className="mr-1.5" /> {busy ? "Sending…" : "Send Test"}
                    </Button>
                </div>

                {lastTest && (
                    <div data-testid="test-result" className="mt-3 rounded border border-slate-200 bg-slate-50 p-3 text-xs">
                        <div className="mb-2 flex flex-wrap gap-3">
                            <span className={`rounded-full px-2 py-0.5 font-bold ${lastTest.sent > 0 ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                                {lastTest.sent > 0 ? "Delivered ✓" : "Not delivered"}
                            </span>
                            <span className="text-slate-600">Subs: {lastTest.subs} · Stale: {String(lastTest.stale)}</span>
                        </div>
                        {lastTest.detail && <div className="mb-2 whitespace-pre-line text-slate-700"><b>Hint:</b> {lastTest.detail}</div>}
                        {lastTest.payload && (
                            <details className="mt-2">
                                <summary className="cursor-pointer font-semibold text-slate-700">Payload sent</summary>
                                <pre className="mt-1 overflow-x-auto rounded bg-white p-2 text-[10px]">{JSON.stringify(lastTest.payload, null, 2)}</pre>
                            </details>
                        )}
                        {lastTest.recent_events?.length > 0 && (
                            <details className="mt-2">
                                <summary className="cursor-pointer font-semibold text-slate-700">Last {lastTest.recent_events.length} delivery attempts</summary>
                                <pre className="mt-1 overflow-x-auto rounded bg-white p-2 text-[10px]">{JSON.stringify(lastTest.recent_events, null, 2)}</pre>
                            </details>
                        )}
                    </div>
                )}
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-500">Recent notifications ({events.length})</div>
                {events.length === 0 ? (
                    <div className="rounded border border-dashed border-slate-300 p-4 text-center text-sm text-slate-500">
                        No notifications yet. Once slips are verified or txns are added, they'll show here.
                    </div>
                ) : (
                    <ul className="divide-y divide-slate-100">
                        {events.map((n, i) => (
                            <li key={n.id || i} className="py-2 text-sm">
                                <div className="flex items-center justify-between gap-3">
                                    <span className="font-semibold text-slate-900">{n.title || n.type || "Notification"}</span>
                                    <span className="text-[10px] text-slate-400">{n.created_at?.slice(0, 16) || ""}</span>
                                </div>
                                {n.body && <div className="text-xs text-slate-600">{n.body}</div>}
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-xs text-amber-900">
                <div className="font-bold uppercase tracking-widest">If sound plays but no banner appears</div>
                <ol className="mt-2 list-decimal space-y-1 pl-4">
                    <li>Android → Settings → Apps → <b>Chrome</b> (or the installed MICM app) → Notifications.</li>
                    <li>Find <b>MICM / teleadmin.in</b> and set importance to <b>Urgent / High</b>.</li>
                    <li>Toggle <b>Show on lock screen: Show all content</b>.</li>
                    <li>Come back here and tap <b>Send Test</b> again.</li>
                </ol>
            </div>
        </div>
    );
}
